import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, TrendingUp, TrendingDown, Activity, RefreshCw, Plus, X } from "lucide-react";

export default function MarketsPanel({ accent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [bonds, setBonds] = useState([]);
  const [newSym, setNewSym] = useState("");
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, i, b] = await Promise.all([
        api.get("/falcon/markets/watchlist"),
        api.get("/falcon/markets/info"),
        api.get("/falcon/markets/bonds"),
      ]);
      setData(w.data); setInfo(i.data); setBonds(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const addSymbol = async () => {
    if (!newSym.trim()) return;
    const syms = [...(data?.symbols || []), newSym.toUpperCase().trim()];
    const r = await api.post("/falcon/markets/watchlist", { symbols: syms });
    setNewSym(""); load();
  };
  const removeSymbol = async (s) => {
    const syms = (data?.symbols || []).filter((x) => x !== s);
    await api.post("/falcon/markets/watchlist", { symbols: syms });
    load();
  };

  if (loading && !data) return <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const liveSource = info?.twelve_data_live ? "Live (Twelve Data)" : "Mock data — set TWELVE_DATA_API_KEY to go live";

  return (
    <div className="space-y-6" data-testid="falcon-markets">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Markets</div>
          <h2 className="font-serif text-3xl mt-1">Global watchlist</h2>
          <div className="text-xs text-muted-foreground mt-1">{liveSource}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(!editing)} data-testid="markets-edit-toggle" className="h-9 px-3 text-xs rounded-sm border border-border/60 hover:bg-secondary">
            {editing ? "Done" : "Edit"}
          </button>
          <button onClick={load} data-testid="markets-refresh" className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border/60 hover:bg-secondary">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {editing && (
        <div className="flex items-center gap-2">
          <input value={newSym} onChange={(e) => setNewSym(e.target.value)} placeholder="Add ticker (e.g. AAPL, ARAMCO)"
            data-testid="markets-add-input"
            className="flex-1 h-9 rounded-sm border border-border bg-background px-3 text-sm" />
          <button onClick={addSymbol} data-testid="markets-add-btn" className="h-9 px-3 rounded-sm text-white text-xs" style={{ background: accent }}>
            <Plus className="h-3 w-3 inline" /> Add
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(data?.quotes || []).map((q) => {
          const up = (q.percent_change || 0) >= 0;
          return (
            <div key={q.symbol} className="rounded-sm border border-border/60 bg-card p-4 relative" data-testid={`market-card-${q.symbol}`}>
              {editing && (
                <button onClick={() => removeSymbol(q.symbol)} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" data-testid={`remove-${q.symbol}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-mono text-sm tracking-wider">{q.symbol}</div>
                  <div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{q.name}</div>
                </div>
                <div className="flex items-center gap-1 text-[11px]" style={{ color: up ? "#22c55e" : "#ef4444" }}>
                  {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {q.percent_change > 0 && "+"}{(q.percent_change || 0).toFixed(2)}%
                </div>
              </div>
              <div className="mt-3 font-mono text-2xl">{q.currency} {(q.price || 0).toFixed(2)}</div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <div>H<div className="text-foreground font-mono normal-case">{(q.high || 0).toFixed(2)}</div></div>
                <div>L<div className="text-foreground font-mono normal-case">{(q.low || 0).toFixed(2)}</div></div>
                <div>Vol<div className="text-foreground font-mono normal-case">{(q.volume || 0).toLocaleString()}</div></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bonds */}
      <div className="mt-8">
        <h3 className="font-display text-base mb-3 flex items-center gap-2"><Activity className="h-4 w-4" /> Sovereign bond yields</h3>
        <div className="rounded-sm border border-border/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-start">
                <th className="text-start font-medium px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">Bond</th>
                <th className="text-start font-medium px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">Yield</th>
                <th className="text-start font-medium px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">Δ bps</th>
              </tr>
            </thead>
            <tbody>
              {bonds.map((b) => (
                <tr key={b.symbol} className="border-t border-border/60" data-testid={`bond-${b.symbol}`}>
                  <td className="px-3 py-2">{b.name}</td>
                  <td className="px-3 py-2 font-mono">{b.yield != null ? b.yield.toFixed(3) : "—"}</td>
                  <td className="px-3 py-2 font-mono" style={{ color: (b.change_bps || 0) >= 0 ? "#22c55e" : "#ef4444" }}>
                    {(b.change_bps || 0) >= 0 ? "+" : ""}{(b.change_bps || 0).toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
